'use client';

import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { ChatRunListResponse, QuotaUsage } from '@open-design/contracts';
import { useT } from '../../i18n';
import { notifyActionError } from '../../state/toasts';
import { UsageSection } from '../UsageSection';

// Phase C-5 Day 3 PM — billing settings UI.
//
// State machine (derived from /api/billing/me):
//   - "free"     → users.plan === 'free'
//   - "pro"      → users.plan === 'pro' && status === 'active' (or trialing)
//   - "pastDue"  → users.plan === 'pro' && status === 'past_due' (Stripe is
//                  dunning; the card on file is bad but we haven't fired
//                  customer.subscription.deleted yet)
//   - "canceled" → users.plan === 'pro' && status === 'canceled' (rare:
//                  briefly between cancel-at-period-end and the deleted
//                  webhook). Falls into the same UI as "past_due" — both
//                  lead to the portal link.
//
// Banner: ?ok=1 (post-checkout) and ?cancel=1 (Stripe cancel) are read once
// from the URL on mount. We dismiss them by replacing the URL — same pattern
// the welcome modal uses for its query-param trigger.

type BillingState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | {
      status: 'ready';
      plan: 'free' | 'pro' | 'unlimited';
      subscriptionStatus: string | null;
      currentPeriodEnd: string | null;
      hasPaymentMethod: boolean;
    };

type BannerKind = 'success' | 'canceled' | null;
const PAYMENT_ATTENTION_STATUSES = new Set([
  'past_due',
  'unpaid',
  'incomplete',
  'incomplete_expired',
]);

interface BillingMeResponse {
  plan?: unknown;
  subscriptionStatus?: unknown;
  currentPeriodEnd?: unknown;
  hasPaymentMethod?: unknown;
}

interface Props {
  email: string;
  /**
   * s44 — when true, the panel skips its own page-chrome (title +
   * email + outer padding) so it can be embedded inside SettingsDialog's
   * "Billing" tab without rendering a redundant header. The standalone
   * `/settings/billing` route leaves this unset and keeps its full chrome.
   */
  embedded?: boolean;
}

function readInitialBanner(): BannerKind {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  if (params.get('ok') === '1') return 'success';
  if (params.get('cancel') === '1') return 'canceled';
  return null;
}

function formatDate(iso: string | null): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function hostedAuthInlineMessage(status: number): string | null {
  if (status === 401) return 'Session expired. Please sign in again.';
  if (status === 403) {
    return 'Access changed. Sign in with an allowed account or contact support.';
  }
  return null;
}

function recoverHostedAuthAction(
  label: string,
  status: number,
  setInlineError: (message: string) => void,
): boolean {
  const message = hostedAuthInlineMessage(status);
  if (!message) return false;
  setInlineError(message);
  notifyActionError(label, { status, message });
  return true;
}

export function BillingPanel({ email, embedded = false }: Props): ReactElement {
  const t = useT();
  const [state, setState] = useState<BillingState>({ status: 'loading' });
  const [banner, setBanner] = useState<BannerKind>(readInitialBanner());
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionInFlight, setActionInFlight] = useState<
    'checkout' | 'portal' | null
  >(null);

  const refresh = useCallback(async () => {
    try {
      const resp = await fetch('/api/billing/me', {
        credentials: 'same-origin',
      });
      if (!resp.ok) {
        // Surface the real failure to the console so we can diagnose
        // "couldn't load billing details" reports without needing the
        // user to crack open devtools. The user-facing copy stays soft.
        let detail = '';
        try {
          detail = await resp.text();
        } catch {
          /* noop */
        }
        // eslint-disable-next-line no-console
        console.error(
          `[billing] GET /api/billing/me failed: ${resp.status} ${resp.statusText}`,
          detail,
        );
        if (
          recoverHostedAuthAction(
            "Couldn't load billing details",
            resp.status,
            (message) => setState({ status: 'error', message }),
          )
        ) {
          return;
        }
        setState({
          status: 'error',
          message: `${t('billing.error')} (${resp.status})`,
        });
        return;
      }
      const body = (await resp.json()) as BillingMeResponse;
      // s43 — accept 'unlimited' as a valid plan. Anything we don't
      // recognise still falls through to 'free' for safety (a future
      // server-side tier rename mustn't make the UI crash on undefined).
      const plan =
        body.plan === 'pro'
          ? 'pro'
          : body.plan === 'unlimited'
            ? 'unlimited'
            : 'free';
      const subscriptionStatus =
        typeof body.subscriptionStatus === 'string'
          ? body.subscriptionStatus
          : null;
      const currentPeriodEnd =
        typeof body.currentPeriodEnd === 'string'
          ? body.currentPeriodEnd
          : null;
      const hasPaymentMethod = body.hasPaymentMethod === true;
      setState({
        status: 'ready',
        plan,
        subscriptionStatus,
        currentPeriodEnd,
        hasPaymentMethod,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[billing] GET /api/billing/me threw:', err);
      setState({
        status: 'error',
        message: `${t('billing.error')} (network)`,
      });
    }
  }, [t]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const dismissBanner = useCallback(() => {
    setBanner(null);
    if (typeof window !== 'undefined' && window.history) {
      const url = new URL(window.location.href);
      url.searchParams.delete('ok');
      url.searchParams.delete('cancel');
      window.history.replaceState(null, '', url.toString());
    }
  }, []);

  const startCheckout = useCallback(
    async (plan: 'pro' | 'unlimited') => {
      setActionInFlight('checkout');
      setActionError(null);
      try {
        const resp = await fetch('/api/billing/checkout', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ plan }),
        });
        if (!resp.ok) {
          if (
            recoverHostedAuthAction(
              "Couldn't start checkout",
              resp.status,
              setActionError,
            )
          ) {
            setActionInFlight(null);
            return;
          }
          setActionError(t('billing.checkoutFailed'));
          setActionInFlight(null);
          return;
        }
        const body = (await resp.json()) as { url?: unknown };
        if (typeof body.url === 'string') {
          window.location.href = body.url;
          // Don't clear actionInFlight — page is navigating away.
          return;
        }
        setActionError(t('billing.checkoutFailed'));
        setActionInFlight(null);
      } catch {
        setActionError(t('billing.checkoutFailed'));
        setActionInFlight(null);
      }
    },
    [t],
  );

  const openPortal = useCallback(async () => {
    setActionInFlight('portal');
    setActionError(null);
    try {
      const resp = await fetch('/api/billing/portal', {
        credentials: 'same-origin',
      });
      if (!resp.ok) {
        if (
          recoverHostedAuthAction(
            "Couldn't open billing portal",
            resp.status,
            setActionError,
          )
        ) {
          setActionInFlight(null);
          return;
        }
        setActionError(t('billing.portalFailed'));
        setActionInFlight(null);
        return;
      }
      const body = (await resp.json()) as { url?: unknown };
      if (typeof body.url === 'string') {
        window.location.href = body.url;
        return;
      }
      setActionError(t('billing.portalFailed'));
      setActionInFlight(null);
    } catch {
      setActionError(t('billing.portalFailed'));
      setActionInFlight(null);
    }
  }, [t]);

  return (
    <main
      className="billing-panel"
      style={embedded ? embeddedPanelStyle : panelStyle}
    >
      {embedded ? null : (
        <header style={headerStyle}>
          <h1 style={titleStyle}>{t('billing.title')}</h1>
          <p style={emailStyle}>{email}</p>
        </header>
      )}

      {/* s44 — Usage merged into Billing so "what plan am I on / how
          many runs left / manage subscription" is one surface. UsageSection
          renders null when no quota snapshot is available, so this is a
          no-op for unauth/offline users. */}
      <UsageSection />

      {banner === 'success' && (
        <div role="status" style={successBannerStyle}>
          <span>{t('billing.banner.success')}</span>
          <button type="button" onClick={dismissBanner} style={dismissBtnStyle}>
            {t('billing.banner.dismiss')}
          </button>
        </div>
      )}
      {banner === 'canceled' && (
        <div role="status" style={canceledBannerStyle}>
          <span>{t('billing.banner.canceled')}</span>
          <button type="button" onClick={dismissBanner} style={dismissBtnStyle}>
            {t('billing.banner.dismiss')}
          </button>
        </div>
      )}

      {state.status === 'loading' && <p>{t('billing.loading')}</p>}

      {state.status === 'error' && (
        <div role="alert" style={errorStyle}>
          {state.message}
        </div>
      )}

      {state.status === 'ready' && (
        <>
          {/*
            Stripe can report several dunning/payment-attention statuses,
            not just `past_due`. Keep them on the same recovery surface so
            users always get the portal action instead of a healthy plan card.
          */}
          <section style={overviewGridStyle} aria-label="Billing overview">
            <PlanFact label="Account" value={email} detail="Signed-in workspace" />
            <PlanFact
              label="Plan"
              value={planLabel(state.plan)}
              detail={statusDetail(state.subscriptionStatus)}
            />
            <PlanFact
              label="Hosted runs"
              value={state.plan === 'free' ? 'Use my API key' : state.plan === 'pro' ? '100 runs / month' : 'Unlimited hosted runs'}
              detail="Your own API key remains unlimited."
            />
            <PlanFact
              label="Key access"
              value={keyAccessValue(state)}
              detail={keyAccessDetail(state)}
            />
            <PlanFact
              label="Billing"
              value={state.hasPaymentMethod ? 'Payment method on file' : 'No payment method'}
              detail={state.currentPeriodEnd ? `Renews or ends ${formatDate(state.currentPeriodEnd)}` : 'Managed from the portal'}
            />
          </section>
          <section style={cardStyle}>
            {state.plan === 'free' && (
              <FreeCard
                onUpgrade={startCheckout}
                busy={actionInFlight === 'checkout'}
              />
            )}
            {(state.plan === 'pro' || state.plan === 'unlimited') &&
              isPaymentAttentionStatus(state.subscriptionStatus) && (
                <PastDueCard
                  onManage={openPortal}
                  busy={actionInFlight === 'portal'}
                />
              )}
            {state.plan === 'pro' &&
              !isPaymentAttentionStatus(state.subscriptionStatus) && (
              <ProCard
                currentPeriodEnd={state.currentPeriodEnd}
                subscriptionStatus={state.subscriptionStatus}
                onManage={openPortal}
                onUpgrade={() => startCheckout('unlimited')}
                upgradeBusy={actionInFlight === 'checkout'}
                busy={actionInFlight === 'portal'}
              />
            )}
            {state.plan === 'unlimited' &&
              !isPaymentAttentionStatus(state.subscriptionStatus) && (
                <UnlimitedCard
                  currentPeriodEnd={state.currentPeriodEnd}
                  subscriptionStatus={state.subscriptionStatus}
                  onManage={openPortal}
                  busy={actionInFlight === 'portal'}
                />
              )}
            {actionError && (
              <p role="alert" style={inlineErrorStyle}>
                {actionError}
              </p>
            )}
          </section>
          <AccountDangerZone />
          <SupportHandoff email={email} />
        </>
      )}
    </main>
  );
}

function planLabel(plan: 'free' | 'pro' | 'unlimited'): string {
  if (plan === 'pro') return 'Pro';
  if (plan === 'unlimited') return 'Unlimited';
  return 'Free';
}

function statusDetail(status: string | null): string {
  if (!status) return 'No Stripe subscription';
  if (status === 'active') return 'Subscription active';
  if (isPaymentAttentionStatus(status)) return 'Payment needs attention';
  if (status === 'canceled') return 'Subscription canceled';
  return status.replace(/_/g, ' ');
}

function isPaymentAttentionStatus(status: string | null): boolean {
  return typeof status === 'string' && PAYMENT_ATTENTION_STATUSES.has(status);
}

function keyAccessValue(state: Extract<BillingState, { status: 'ready' }>): string {
  if (state.plan === 'free') return 'My API key';
  if (isPaymentAttentionStatus(state.subscriptionStatus)) return 'Hosted key paused';
  return 'Hosted key or my API key';
}

function keyAccessDetail(state: Extract<BillingState, { status: 'ready' }>): string {
  if (state.plan === 'free') return 'Free accounts run with your own provider key.';
  if (isPaymentAttentionStatus(state.subscriptionStatus)) {
    return 'Update payment to keep hosted key access healthy.';
  }
  return 'Switch between the hosted key and your own key in Settings.';
}

function PlanFact({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail: string;
}): ReactElement {
  return (
    <div style={overviewItemStyle}>
      <span style={overviewLabelStyle}>{label}</span>
      <strong style={overviewValueStyle}>{value}</strong>
      <span style={overviewDetailStyle}>{detail}</span>
    </div>
  );
}

// GDPR self-service: export + delete. Inline English for now — i18n keys
// land alongside the broader Pre-public-signup native-speaker review pass
// (already tracked in CLAUDE.md → Roadmap to GA).
function AccountDangerZone(): ReactElement {
  const [busy, setBusy] = useState<'export' | 'delete' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onExport = async () => {
    setBusy('export');
    setError(null);
    try {
      // GET returns JSON with Content-Disposition: attachment, so browsing
      // straight to it triggers a download. Using window.location.href
      // rather than fetch() keeps the binary off the React tree.
      window.location.href = '/api/users/me/export';
    } catch {
      setError("Couldn't export your data. Try again.");
    } finally {
      setBusy(null);
    }
  };

  const onDelete = async () => {
    const confirmed = window.confirm(
      'Permanently delete your account and all your projects, conversations, and data?\n\nThis cannot be undone.',
    );
    if (!confirmed) return;
    setBusy('delete');
    setError(null);
    try {
      const resp = await fetch('/api/users/me', {
        method: 'DELETE',
        credentials: 'same-origin',
      });
      if (!resp.ok) {
        if (
          recoverHostedAuthAction(
            "Couldn't delete account",
            resp.status,
            setError,
          )
        ) {
          setBusy(null);
          return;
        }
        const body = (await resp.json().catch(() => ({}))) as {
          error?: string;
        };
        if (body.error === 'cannot_delete_owner') {
          setError(
            "You're the workspace owner — transfer ownership before deleting.",
          );
        } else {
          setError("Couldn't delete your account. Try again or contact support.");
        }
        setBusy(null);
        return;
      }
      const body = (await resp.json()) as {
        stripeSubscriptionId?: string | null;
      };
      // Optional warning if Stripe subscription was orphaned. We surface
      // this inline rather than blocking the redirect because the account
      // is already deleted server-side; the user has to act in Stripe to
      // stop future charges.
      if (body.stripeSubscriptionId) {
        window.alert(
          'Your account was deleted, but your Stripe subscription is still active. Cancel it from your bank statement or contact support to stop future charges.',
        );
      }
      // Session cookie was cleared server-side; bounce to /login.
      window.location.href = '/login';
    } catch {
      setError("Couldn't delete your account. Try again or contact support.");
      setBusy(null);
    }
  };

  return (
    <section style={dangerZoneStyle} aria-label="Account">
      <h2 style={cardHeadingStyle}>Account controls</h2>
      <p style={dangerIntroStyle}>
        Export is reversible. Account deletion is permanent and removes saved
        projects, conversations, and generated files.
      </p>

      <div style={dangerRowStyle}>
        <div>
          <p style={dangerLineStyle}>Download your data</p>
          <p style={dangerSubStyle}>
            Export everything we have for your account as a JSON file.
          </p>
        </div>
        <button
          type="button"
          onClick={onExport}
          disabled={busy !== null}
          className="billing-action billing-action--secondary"
        >
          {busy === 'export' ? 'Loading…' : 'Download'}
        </button>
      </div>

      <div style={{ ...dangerRowStyle, marginTop: 16 }}>
        <div>
          <p style={dangerLineStyle}>Delete account</p>
          <p style={dangerSubStyle}>
            Permanently remove your account, projects, and conversations.
          </p>
        </div>
        <button
          type="button"
          onClick={onDelete}
          disabled={busy !== null}
          className="billing-action billing-action--destructive"
        >
          {busy === 'delete' ? 'Deleting…' : 'Delete'}
        </button>
      </div>

      {error && (
        <p role="alert" style={inlineErrorStyle}>
          {error}
        </p>
      )}
    </section>
  );
}

function SupportHandoff({ email }: { email: string }): ReactElement {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const copySupportContext = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const [billing, usage, runs] = await Promise.all([
        fetchJsonSoft<BillingMeResponse>('/api/billing/me'),
        fetchJsonSoft<QuotaUsage>('/api/users/me/usage'),
        fetchJsonSoft<ChatRunListResponse>('/api/runs'),
      ]);
      const context = {
        product: 'Design Work',
        generatedAt: new Date().toISOString(),
        account: {
          email,
          plan: billing?.plan ?? null,
          subscriptionStatus: billing?.subscriptionStatus ?? null,
          hasPaymentMethod: billing?.hasPaymentMethod ?? null,
        },
        usage: usage
          ? {
              plan: usage.plan,
              used: usage.used,
              limit: usage.limit,
              resetsAt: usage.resetsAt,
              override: usage.override,
            }
          : null,
        page: typeof window !== 'undefined' ? window.location.href : null,
        userAgent:
          typeof navigator !== 'undefined' ? navigator.userAgent : null,
        recentRuns: Array.isArray(runs?.runs)
          ? runs.runs.slice(0, 5).map((run) => ({
              id: run.id,
              status: run.status,
              projectId: run.projectId ?? null,
              conversationId: run.conversationId ?? null,
            }))
          : [],
      };
      const text = JSON.stringify(context, null, 2);
      await navigator.clipboard?.writeText(text);
      setMessage('Support context copied. Paste it into your support email.');
    } catch {
      setMessage("Couldn't copy support context. Contact support and include your account email.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section style={supportHandoffStyle} aria-label="Support">
      <h2 style={cardHeadingStyle}>Support handoff</h2>
      <p style={dangerIntroStyle}>
        Copy a diagnostic bundle with your account, billing, usage, current
        page, and recent run context. It does not include prompts, API keys, or
        generated files.
      </p>
      <div style={dangerRowStyle}>
        <div>
          <p style={dangerLineStyle}>Current account context</p>
          <p style={dangerSubStyle}>
            Include this when payment, quota, or generation access needs help.
          </p>
        </div>
        <a
          href={`mailto:support@design.work?subject=${encodeURIComponent(`Design Work support for ${email}`)}`}
          className="billing-action billing-action--link"
        >
          Contact support
        </a>
        <button
          type="button"
          onClick={() => void copySupportContext()}
          disabled={busy}
          className="billing-action billing-action--secondary"
        >
          {busy ? 'Copying...' : 'Copy support context'}
        </button>
      </div>
      {message ? (
        <p role="status" style={inlineSuccessStyle}>
          {message}
        </p>
      ) : null}
    </section>
  );
}

async function fetchJsonSoft<T>(url: string): Promise<T | null> {
  try {
    const resp = await fetch(url, { credentials: 'same-origin' });
    if (!resp.ok) return null;
    return (await resp.json()) as T;
  } catch {
    return null;
  }
}

interface FreeProps {
  onUpgrade: (plan: 'pro' | 'unlimited') => void;
  busy: boolean;
}

function FreeCard({ onUpgrade, busy }: FreeProps): ReactElement {
  const t = useT();
  return (
    <div data-testid="billing-free-card">
      <h2 style={cardHeadingStyle}>{t('billing.free.heading')}</h2>
      <p style={cardLedeStyle}>{t('billing.free.lede')}</p>
      <div style={tierRowStyle}>
        <button
          type="button"
          onClick={() => onUpgrade('pro')}
          disabled={busy}
          className="billing-action billing-action--primary"
          data-testid="billing-upgrade-pro"
        >
          {busy ? t('billing.loading') : t('billing.free.cta')}
        </button>
        <button
          type="button"
          onClick={() => onUpgrade('unlimited')}
          disabled={busy}
          className="billing-action billing-action--secondary"
          data-testid="billing-upgrade-unlimited"
        >
          {busy ? t('billing.loading') : t('billing.free.ctaUnlimited')}
        </button>
      </div>
    </div>
  );
}

interface ProProps {
  currentPeriodEnd: string | null;
  subscriptionStatus: string | null;
  onManage: () => void;
  /** s43 — also offer in-flight upgrade to Unlimited from the Pro card. */
  onUpgrade: () => void;
  busy: boolean;
  upgradeBusy: boolean;
}

function ProCard({
  currentPeriodEnd,
  subscriptionStatus,
  onManage,
  onUpgrade,
  busy,
  upgradeBusy,
}: ProProps): ReactElement {
  const t = useT();
  const dateFormatted = formatDate(currentPeriodEnd);
  const renewLine =
    subscriptionStatus === 'canceled'
      ? t('billing.subscriptionStatus.canceled')
      : dateFormatted
        ? t('billing.pro.renewing').replace('{date}', dateFormatted)
        : '';
  return (
    <div data-testid="billing-pro-card">
      <h2 style={cardHeadingStyle}>{t('billing.pro.heading')}</h2>
      {renewLine && <p style={cardLedeStyle}>{renewLine}</p>}
      <div style={tierRowStyle}>
        <button
          type="button"
          onClick={onManage}
          disabled={busy}
          className="billing-action billing-action--secondary"
        >
          {busy ? t('billing.loading') : t('billing.pro.cta')}
        </button>
        <button
          type="button"
          onClick={onUpgrade}
          disabled={upgradeBusy}
          className="billing-action billing-action--primary"
          data-testid="billing-pro-upgrade-unlimited"
        >
          {upgradeBusy ? t('billing.loading') : t('billing.pro.upgradeUnlimited')}
        </button>
      </div>
    </div>
  );
}

interface UnlimitedProps {
  currentPeriodEnd: string | null;
  subscriptionStatus: string | null;
  onManage: () => void;
  busy: boolean;
}

function UnlimitedCard({
  currentPeriodEnd,
  subscriptionStatus,
  onManage,
  busy,
}: UnlimitedProps): ReactElement {
  const t = useT();
  const dateFormatted = formatDate(currentPeriodEnd);
  const renewLine =
    subscriptionStatus === 'canceled'
      ? t('billing.subscriptionStatus.canceled')
      : dateFormatted
        ? t('billing.unlimited.renewing').replace('{date}', dateFormatted)
        : '';
  return (
    <div data-testid="billing-unlimited-card">
      <h2 style={cardHeadingStyle}>{t('billing.unlimited.heading')}</h2>
      {renewLine && <p style={cardLedeStyle}>{renewLine}</p>}
      <button
        type="button"
        onClick={onManage}
        disabled={busy}
        className="billing-action billing-action--secondary"
      >
        {busy ? t('billing.loading') : t('billing.unlimited.cta')}
      </button>
    </div>
  );
}

interface PastDueProps {
  onManage: () => void;
  busy: boolean;
}

function PastDueCard({ onManage, busy }: PastDueProps): ReactElement {
  const t = useT();
  return (
    <div data-testid="billing-pastdue-card" style={pastDueWrapStyle}>
      <h2 style={cardHeadingStyle}>{t('billing.pastDue.heading')}</h2>
      <p style={cardLedeStyle}>{t('billing.pastDue.lede')}</p>
      <button
        type="button"
        onClick={onManage}
        disabled={busy}
        className="billing-action billing-action--primary"
      >
        {busy ? t('billing.loading') : t('billing.pastDue.cta')}
      </button>
    </div>
  );
}

// Inline styles keep this component self-contained — no new CSS file to
// thread through the build. The shared design tokens live on
// :root in apps/web/src/index.css, accessed via var(--...). Falls back to
// hardcoded values if the variable isn't set (test envs).
const panelStyle: React.CSSProperties = {
  maxWidth: 640,
  margin: '0 auto',
  padding: '48px 24px',
  fontFamily:
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Inter, system-ui, sans-serif",
  color: 'var(--text, #1a1a1a)',
};
// s44 — embedded variant for SettingsDialog. Drops the outer page padding
// + maxWidth/centering since the dialog's settings-content shell already
// supplies those, and keeps the dialog's font cascade intact.
const embeddedPanelStyle: React.CSSProperties = {
  margin: 0,
  padding: 0,
  color: 'var(--text, #1a1a1a)',
};
const headerStyle: React.CSSProperties = { marginBottom: 32 };
const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 28,
  fontWeight: 500,
  letterSpacing: '-0.01em',
};
const emailStyle: React.CSSProperties = {
  margin: '4px 0 0',
  fontSize: 14,
  color: 'var(--text-muted, #6f6f6f)',
};
const cardStyle: React.CSSProperties = {
  border: '1px solid var(--border, #ebe6df)',
  borderRadius: 8,
  padding: 24,
  background: 'var(--bg-panel)',
};
const overviewGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
  gap: 10,
  marginBottom: 14,
};
const overviewItemStyle: React.CSSProperties = {
  minWidth: 0,
  padding: '14px 16px',
  border: '1px solid var(--border, #ebe6df)',
  borderRadius: 8,
  background: 'var(--bg-panel)',
};
const overviewLabelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: 6,
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--text-muted, #6f6f6f)',
};
const overviewValueStyle: React.CSSProperties = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: 'var(--text-strong, #1a1a1a)',
  fontSize: 14,
};
const overviewDetailStyle: React.CSSProperties = {
  display: 'block',
  marginTop: 4,
  color: 'var(--text-muted, #6f6f6f)',
  fontSize: 12,
  lineHeight: 1.4,
};
const cardHeadingStyle: React.CSSProperties = {
  margin: '0 0 8px',
  fontSize: 20,
  fontWeight: 500,
};
const cardLedeStyle: React.CSSProperties = {
  margin: '0 0 24px',
  fontSize: 15,
  color: 'var(--text-muted, #3a3a3a)',
};
const successBannerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px',
  marginBottom: 24,
  borderRadius: 8,
  background: '#ebf3eb',
  color: '#2c5e2c',
  fontSize: 14,
};
const canceledBannerStyle: React.CSSProperties = {
  ...successBannerStyle,
  background: '#f5f0e8',
  color: '#7a5d2c',
};
const pastDueWrapStyle: React.CSSProperties = {
  borderLeft: '3px solid var(--accent-strong, #b45a3b)',
  paddingLeft: 16,
};
const tierRowStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 10,
  marginTop: 12,
};
const dismissBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'inherit',
  cursor: 'pointer',
  fontSize: 13,
  textDecoration: 'underline',
  padding: 0,
};
const errorStyle: React.CSSProperties = {
  padding: 16,
  borderRadius: 8,
  background: '#fdecec',
  color: '#a02828',
  fontSize: 14,
};
const inlineErrorStyle: React.CSSProperties = {
  margin: '16px 0 0',
  fontSize: 13,
  color: '#a02828',
};
const inlineSuccessStyle: React.CSSProperties = {
  margin: '16px 0 0',
  fontSize: 13,
  color: '#2c5e2c',
};
const dangerZoneStyle: React.CSSProperties = {
  marginTop: 18,
  padding: 20,
  border: '1px solid var(--red-border, #efc8c8)',
  borderRadius: 8,
  background: 'var(--bg-panel)',
};
const supportHandoffStyle: React.CSSProperties = {
  marginTop: 18,
  padding: 20,
  border: '1px solid var(--border, #ebe6df)',
  borderRadius: 8,
  background: 'var(--bg-panel)',
};
const dangerIntroStyle: React.CSSProperties = {
  margin: '-2px 0 16px',
  color: 'var(--text-muted, #6f6f6f)',
  fontSize: 13,
  lineHeight: 1.5,
};
const dangerRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
};
const dangerLineStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 14,
  fontWeight: 500,
};
const dangerSubStyle: React.CSSProperties = {
  margin: '4px 0 0',
  fontSize: 13,
  color: 'var(--text-muted, #6f6f6f)',
};
